import '@fontsource-variable/bricolage-grotesque/opsz.css';
import '@fontsource-variable/instrument-sans/wght.css';
import './styles/global.css';

import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient, onlineManager } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { App } from './App';
import { pb } from './lib/pb';
import { registerMutations } from './lib/queries';
import { ToastProvider } from './lib/toast';

const WEEK = 7 * 24 * 60 * 60 * 1000;

const queryClient = new QueryClient({
  defaultOptions: {
    // kept long enough to open the list offline after a few days; always
    // refetched on start so a failed refetch shows the connection banner
    queries: { gcTime: WEEK, retry: 1 },
  },
});

// TanStack assumes "online" until the first offline event; an app opened
// without a connection would otherwise try (and fail) every change at once.
onlineManager.setOnline(navigator.onLine);
registerMutations(queryClient);

// The last loaded list and changes still waiting for the connection live in
// IndexedDB, so the app opens offline and sends them after a restart.
const persister = createAsyncStoragePersister({
  storage: { getItem: get, setItem: set, removeItem: del },
  key: 'closetly-cache',
});

function logout() {
  pb.authStore.clear();
  queryClient.clear();
  persister.removeClient();
}

registerSW({ immediate: true });

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister, maxAge: WEEK, buster: 'v1' }}
      onSuccess={() => queryClient.resumePausedMutations()}
    >
      <ToastProvider>
        <App onLogout={logout} />
      </ToastProvider>
    </PersistQueryClientProvider>
  </StrictMode>,
);
